import {useEffect, useRef} from 'react';
import lazyLoader from '@ui/utils/loaders/lazy-loader';
import {useSettings} from '@ui/settings/use-settings';
import {RecaptchaAction} from '@common/core/settings/base-backend-settings';

/**
 * Returns whether the v2 "I'm not a robot" checkbox should be shown/enforced
 * for a given action, plus the configured site key.
 *
 * NOTE: this only controls the UI. The authoritative check is server-side
 * (App\Http\Middleware\VerifyRecaptcha) — the form simply must not submit
 * without a token when this is enabled.
 */
export function useRecaptchaV2(action: RecaptchaAction) {
  const {recaptcha: {site_key, enable} = {}} = useSettings();
  return {
    enabled: !!(site_key && enable?.[action]),
    siteKey: site_key,
  };
}

interface Props {
  siteKey: string;
  // called with the token when solved, or null when expired/errored/reset
  onVerify: (token: string | null) => void;
  className?: string;
}

export function RecaptchaV2Checkbox({siteKey, onVerify, className}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    lazyLoader
      .loadAsset(
        'https://www.google.com/recaptcha/api.js?render=explicit',
        {id: 'recaptcha-v2-js'},
      )
      .then(() => {
        const grecaptcha = (window as any).grecaptcha;
        const render = () => {
          if (
            cancelled ||
            !containerRef.current ||
            widgetIdRef.current !== null
          ) {
            return;
          }
          if (!grecaptcha?.render) {
            window.setTimeout(render, 100);
            return;
          }
          widgetIdRef.current = grecaptcha.render(containerRef.current, {
            sitekey: siteKey,
            callback: (token: string) => onVerify(token),
            'expired-callback': () => onVerify(null),
            'error-callback': () => onVerify(null),
          });
        };
        if (grecaptcha?.ready) {
          grecaptcha.ready(render);
        } else {
          render();
        }
      });

    return () => {
      cancelled = true;
    };
    // re-render only if the site key changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  return <div ref={containerRef} className={className} />;
}
